import { IDashboardColumn } from 'types/dashboard';

import { L10nForecasting } from 'l10/forecasting';

/**
 * Synthetic columns injected into the forecast submission deal selection table.
 * These are not returned by the dashboard columns API and are defined here for ordering.
 */
export const FORECAST_RISKS_COLUMN: IDashboardColumn = {
  uuid: 'forecast_risks',
  order: 5,
  isDefault: false,
  groupName: 'Deal',
  objectType: 'deal',
  isEditable: false,
  isFilterable: false,
  sortKey: 'forecast_risks',
  sortOrder: null,
  orderBy: { ruleUid: 'forecast_risks', ruleObjectType: '' },
  filterKey: null,
  referenceKey: null,
  avField: {
    uuid: 'forecast_risks',
    fieldLabel: 'Forecast risks',
    dataType: 'string',
    fieldType: 'forecastRisks',
    fieldClass: 'forecastRisks'
  }
};

export const DEAL_RISK_SCORE_COLUMN: IDashboardColumn = {
  uuid: 'deal_risk',
  order: 6,
  isDefault: false,
  groupName: 'Deal',
  objectType: 'deal',
  isEditable: false,
  isFilterable: false,
  sortKey: 'risk_score',
  sortOrder: null,
  orderBy: { ruleUid: 'deal_risk', ruleObjectType: '' },
  filterKey: null,
  referenceKey: null,
  avField: {
    uuid: 'deal_risk',
    fieldLabel: 'Risk Score',
    dataType: 'number',
    fieldType: 'dealRisk',
    fieldClass: 'dealRisk'
  }
};

export const QUALIFICATION_SCORE_COLUMN: IDashboardColumn = {
  uuid: 'ai_methodology_score',
  order: 7,
  isDefault: false,
  groupName: 'Deal',
  objectType: 'deal',
  isEditable: false,
  isFilterable: false,
  sortKey: 'deal_qualification_score',
  sortOrder: null,
  orderBy: { ruleUid: 'deal_qualification_score', ruleObjectType: '' },
  filterKey: null,
  referenceKey: null,
  fieldType: 'aiMethodology',
  avField: {
    uuid: 'ai_methodology_score',
    fieldLabel: 'Qualification Score',
    dataType: 'string',
    fieldType: 'aiMethodology',
    fieldClass: 'aiMethodology'
  }
} as IDashboardColumn;

/**
 * Whether this column is the primary deal amount column on the forecast submission deal table.
 * Kept in sync with DealSelectionTable column ordering (amount slot, excluding weighted).
 * Used to render `deal_amount` from `filter_deals_reserved_dv` instead of per-field cell payloads.
 */
export const isForecastSubmissionDealAmountDisplayColumn = (column: {
  uuid?: string;
  sortKey?: string | null;
  label?: string;
  fieldType?: string;
  avField?: { fieldLabel?: string } | null;
  avomaField?: { fieldLabel?: string } | null;
  crmField?: { fieldLabel?: string } | null;
}): boolean => {
  if (
    column.uuid === 'weighted_amount' ||
    column.fieldType === 'weightedAmount'
  ) {
    return false;
  }
  const avomaLike = column.avField ?? column.avomaField;
  const label = (
    avomaLike?.fieldLabel ??
    column.crmField?.fieldLabel ??
    column.label ??
    ''
  ).toLowerCase();
  return (
    column.sortKey === 'amount' ||
    column.sortKey === 'total_amount' ||
    (/amount/.test(label) && !label.includes('weighted'))
  );
};

/**
 * Whether this is the primary forecast deal amount column.
 * This is the exact deal amount slot, not any label that merely contains "amount".
 */
export const isForecastSubmissionPrimaryAmountColumn = (column: {
  uuid?: string | null;
  sortKey?: string | null;
  label?: string | null;
  fieldType?: string | null;
  referenceKey?: string | null;
  avField?: { fieldLabel?: string | null } | null;
  avomaField?: {
    fieldLabel?: string | null;
    uuid?: string | null;
    externalId?: string | null;
    fieldId?: string | null;
  } | null;
  crmField?: { fieldLabel?: string | null } | null;
}): boolean => {
  if (
    column.uuid === 'weighted_amount' ||
    column.fieldType === 'weightedAmount'
  ) {
    return false;
  }

  const avomaLike = column.avField ?? column.avomaField;
  const label = (
    avomaLike?.fieldLabel ??
    column.crmField?.fieldLabel ??
    column.label ??
    ''
  )
    .trim()
    .toLowerCase();

  return (
    column.uuid === 'deal_amount' ||
    column.sortKey === 'total_amount' ||
    column.referenceKey === 'amount' ||
    label === 'amount' ||
    /^amount\s*\(/.test(label)
  );
};

/** Column for weighted amount from filter_deals_reserved_dv API. Shown only when forecast setting has is_weight_amount true. */
export const WEIGHTED_AMOUNT_COLUMN: IDashboardColumn = {
  uuid: 'weighted_amount',
  order: 8,
  isDefault: false,
  groupName: 'Deal',
  objectType: 'deal',
  isEditable: false,
  isFilterable: false,
  sortKey: 'weighted_amount',
  sortOrder: null,
  orderBy: { ruleUid: 'weighted_amount', ruleObjectType: '' },
  filterKey: null,
  referenceKey: null,
  headerTooltip: L10nForecasting.settings.weightedAmountTooltip,
  avField: {
    uuid: 'weighted_amount',
    fieldLabel: 'Weighted amount',
    dataType: 'number',
    fieldType: 'weightedAmount',
    fieldClass: 'weightedAmount'
  }
} as IDashboardColumn;
